import { useEffect, useState } from 'react';
import { Empty, Modal, Table, Tag, Typography } from 'antd';
import moment from 'moment';
import { useFreeTrialChanges } from '../../../../../../../Hooks/TT_ELD/Companies';

const { Text } = Typography;

const PAGE_SIZE = 10;

const FIELD_LABELS: Record<string, string> = {
  freeTrial: 'Free trial',
  quantity: 'Quantity',
  startDate: 'Start date',
  endDate: 'End date',
  days: 'Days',
  note: 'Note',
};
const DATE_FIELDS = ['startDate', 'endDate'];

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  'subscription-create-free-trial': { label: 'Opened', color: 'green' },
  'subscription-convert-free-trial': { label: 'Converted', color: 'blue' },
};

// e.g. "subscription-update-free-trial" -> "Update free trial"
const humanizeStatus = (status: string) => {
  const text = status.replace(/^subscription-/, '').replace(/[-_]/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const parseChanges = (text?: string): Record<string, { from?: any; to?: any }> => {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
};

const formatValue = (field: string, value: any) => {
  if (value === null || value === undefined || value === '' || value === 'N/A') return 'N/A';
  if (DATE_FIELDS.includes(field) && moment(value).isValid()) {
    return moment(value).format('YYYY-MM-DD HH:mm');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const columns = [
  {
    title: 'Changed At',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 150,
    render: (date?: string) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-'),
  },
  {
    title: 'Action',
    dataIndex: 'status',
    key: 'status',
    render: (status?: string) => {
      if (!status) return '-';
      const known = STATUS_LABELS[status];
      return <Tag color={known?.color ?? 'orange'}>{known?.label ?? humanizeStatus(status)}</Tag>;
    },
  },
  {
    title: 'What Changed',
    dataIndex: 'text',
    key: 'text',
    render: (text?: string) => {
      const changes = Object.entries(parseChanges(text));
      if (!changes.length) return '-';
      return (
        <div>
          {changes.map(([field, change]) => (
            <div key={field}>
              <Text strong>{FIELD_LABELS[field] ?? field}:</Text>{' '}
              {formatValue(field, change?.from)} → {formatValue(field, change?.to)}
            </div>
          ))}
        </div>
      );
    },
  },
  {
    title: 'Changed By',
    dataIndex: 'editedBy',
    key: 'editedBy',
    render: (editedBy: any) => {
      if (!editedBy) return '-';
      const fullName = [editedBy.first_name, editedBy.second_name].filter(Boolean).join(' ');
      return (
        <div>
          <div>{fullName || '-'}</div>
          {editedBy.email && <Text type="secondary">{editedBy.email}</Text>}
          {editedBy.admin_type && (
            <div>
              <Tag style={{ marginTop: 4 }}>{editedBy.admin_type}</Tag>
            </div>
          )}
        </div>
      );
    },
  },
];

interface Props {
  companyId: string | number | undefined;
  freeTrialId: string | number | undefined;
  open: boolean;
  onClose(): void;
}

const FreeTrialChangesModal = ({ companyId, freeTrialId, open, onClose }: Props) => {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [freeTrialId]);

  const { data, isLoading, isFetching } = useFreeTrialChanges(
    companyId,
    open ? freeTrialId : undefined,
    page,
  );

  const dataSource = data?.data?.map((row: any, index: number) => ({
    ...row,
    key: row.id ?? index,
  }));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title={`Free trial changes${data?.count ? ` (${data.count})` : ''}`}
      destroyOnHidden
    >
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={isLoading || isFetching}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <Empty description="No changes recorded" /> }}
        pagination={{
          total: data?.count,
          current: page,
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
          onChange: setPage,
          hideOnSinglePage: true,
        }}
      />
    </Modal>
  );
};

export default FreeTrialChangesModal;
